import { useEffect, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";

import {
  PasskeyDescription,
  completePasskeyRegistration,
  deletePasskey,
  listPasskeys,
  startPasskeyRegistration,
} from "../../auth/cognito";
import { isDevAuthEnabled } from "../../auth/session";

// Settings → Security panel for passkey lifecycle. Lists the user's
// enrolled credentials (Cognito-issued friendly names — no rename API,
// see docs/RIoT-FOTA_Phase-B_Passkeys.md §2), lets them register a
// new one, and delete existing ones with a confirm step.

export function PasskeysPage() {
  // Dev-bypass mode: the SPA short-circuits sign-in for local
  // exploration, so there's no real Cognito access token to drive the
  // passkey APIs. Showing a banner is friendlier than letting every
  // call 400 — until MSW grows handlers for /auth/webauthn/*.
  if (isDevAuthEnabled()) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="mb-2 text-2xl font-semibold text-white">Passkeys</h1>
        <div role="status" className="rounded-xl border border-amber-500/30 bg-amber-950/30 px-4 py-3 text-sm text-amber-200">
          Passkey management is unavailable in local dev mode (no real Cognito session).
          Run against the dev or prod API to register and verify passkeys.
        </div>
      </div>
    );
  }
  return <PasskeysPageInner />;
}

function PasskeysPageInner() {
  const [credentials, setCredentials] = useState<PasskeyDescription[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const list = await listPasskeys();
      setCredentials(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load passkeys");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function onRegister() {
    setError(null);
    setInfo(null);
    setRegistering(true);
    try {
      const optionsJSON = await startPasskeyRegistration();
      const credential = await startRegistration({ optionsJSON });
      await completePasskeyRegistration(credential);
      setInfo("Passkey registered successfully.");
      await refresh();
    } catch (err) {
      if (err instanceof Error && err.name === "NotAllowedError") {
        setError("Passkey enrolment was cancelled.");
      } else {
        setError(err instanceof Error ? err.message : "Could not register passkey");
      }
    } finally {
      setRegistering(false);
    }
  }

  async function onDelete(credentialId: string) {
    if (!window.confirm("Remove this passkey? You won't be able to sign in with it again.")) return;
    setError(null);
    setInfo(null);
    setDeletingId(credentialId);
    try {
      await deletePasskey(credentialId);
      setInfo("Passkey removed.");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete passkey");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <h1 className="mb-2 text-2xl font-semibold text-white">Passkeys</h1>
      <p className="mb-6 text-sm text-riot-navy-200/80">
        Sign in to RIoT-FOTA with Touch ID, Face ID, Windows Hello, or a hardware security
        key. Passkeys are phishing-resistant and never leave your device.
      </p>

      <div className="mb-6 flex items-center justify-between">
        <button
          type="button"
          onClick={onRegister}
          disabled={registering}
          className="rounded-xl bg-riot-gold-400 px-4 py-2 text-sm font-semibold text-riot-navy-950 transition hover:bg-riot-gold-300 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {registering ? "Following your device prompt…" : "Register a new passkey"}
        </button>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="text-sm text-riot-gold-300 hover:text-riot-gold-200 disabled:opacity-60"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded-xl border border-red-500/25 bg-red-950/35 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}
      {info && (
        <div role="status" className="mb-4 rounded-xl border border-emerald-500/25 bg-emerald-950/30 px-3 py-2 text-xs text-emerald-200">
          {info}
        </div>
      )}

      {!loading && credentials.length === 0 && (
        <p className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-6 text-center text-sm text-riot-navy-200/70">
          No passkeys registered yet. Click <span className="font-semibold">Register a new passkey</span> above to enrol one.
        </p>
      )}

      {credentials.length > 0 && (
        <ul className="space-y-3">
          {credentials.map((cred) => (
            <li
              key={cred.credentialId}
              className="flex items-start justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-white">{cred.friendlyName}</div>
                <div className="mt-0.5 text-[11px] text-riot-navy-300">
                  Added {cred.createdAt}
                  {cred.authenticatorAttachment ? ` · ${cred.authenticatorAttachment}` : ""}
                  {cred.authenticatorTransports?.length ? ` · ${cred.authenticatorTransports.join(", ")}` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onDelete(cred.credentialId)}
                disabled={deletingId === cred.credentialId}
                className="rounded-lg border border-red-500/30 px-3 py-1.5 text-xs text-red-200 transition hover:bg-red-950/40 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingId === cred.credentialId ? "Removing…" : "Remove"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
